// src/peer-manager.ts
import Peer, { type DataConnection } from 'peerjs';
import {
  SIGNAL_HOST,
  SIGNAL_PORT,
  SIGNAL_PATH,
  SIGNAL_SECURE,
  LOCATION_THROTTLE_MS,
} from './config';
import {
  parseMessage,
  serializeMessage,
  type P2PMessage,
  type LocationMessage,
} from './protocol';

export type PeerCallback = (msg: P2PMessage, fromPeerId: string) => void;
export type StatusCallback = (
  status: 'connecting' | 'connected' | 'disconnected' | 'error',
  detail?: string,
) => void;
/** Called when a new data channel to a peer first opens. Use this to immediately send current state to that peer. */
export type PeerOpenCallback = (peerId: string) => void;

function normalizeSignalPath(path: string): string {
  let normalized = path.startsWith('/') ? path : `/${path}`;
  if (!normalized.endsWith('/')) normalized += '/';
  return normalized;
}

/**
 * Throttle location broadcasts: pass through at most once per intervalMs.
 */
export function throttleLocation(
  fn: (msg: LocationMessage) => void,
  intervalMs: number,
): (msg: LocationMessage) => void {
  let lastSent = 0;
  return (msg: LocationMessage) => {
    const now = Date.now();
    if (now - lastSent >= intervalMs) {
      lastSent = now;
      fn(msg);
    }
  };
}

const REDIAL_DELAYS = [2_000, 5_000, 15_000];
/** After the first few tries, keep dialing for the rest of the trip. */
export const REDIAL_STEADY_MS = 30_000;

/** Delay before the next dial. Attempts are 1-based. Never gives up. */
export function redialDelay(attempt: number): number {
  if (attempt < 1) return REDIAL_DELAYS[0]!;
  return REDIAL_DELAYS[attempt - 1] ?? REDIAL_STEADY_MS;
}

const TRANSIENT_PEER_ERRORS = new Set([
  'peer-unavailable',
  'network',
  'server-error',
  'socket-error',
  'socket-closed',
  'webrtc',
  'disconnected',
]);

export class PeerManager {
  private peer: Peer | null = null;
  private connections = new Map<string, DataConnection>();
  private roomCode: string = '';
  private destroyed = false;
  private redialAttempts = new Map<string, number>();
  private redialTimers = new Map<string, number>();
  private knownPeers = new Set<string>();
  private openedAt = new Map<string, number>();
  private signalingTimer: number | null = null;
  private peerListTimer: number | null = null;
  private readonly onOnline = (): void => this.recover();

  constructor(
    private readonly onMessage: PeerCallback,
    private readonly onStatus: StatusCallback,
    private readonly onPeerOpen?: PeerOpenCallback,
  ) {}

  connect(roomCode: string): void {
    this.destroyed = false;
    this.roomCode = roomCode.toUpperCase();
    window.addEventListener('online', this.onOnline);
    this.onStatus('connecting');
    void this.startPeer();
  }

  /** Fetch TURN creds from the Worker, then open the PeerJS connection. */
  private async startPeer(): Promise<void> {
    // Minimal STUN fallback if the Worker's /ice endpoint is unreachable.
    // The real TURN/STUN list should come from the Worker.
    let iceServers: RTCIceServer[] = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ];

    try {
      const protocol = SIGNAL_SECURE ? 'https' : 'http';
      const res = await fetch(
        `${protocol}://${SIGNAL_HOST}:${SIGNAL_PORT}/ice`,
      );
      if (res.ok) {
        const body = (await res.json()) as { iceServers?: RTCIceServer[] };
        if (Array.isArray(body.iceServers) && body.iceServers.length > 0) {
          iceServers = body.iceServers;
        }
      }
    } catch {
      /* fall through to STUN-only fallback */
    }

    this.peer = new Peer({
      host: SIGNAL_HOST,
      port: SIGNAL_PORT,
      path: SIGNAL_PATH,
      secure: SIGNAL_SECURE,
      key: this.roomCode, // PeerJS sends key as a WS query param; Worker uses it to key the DO per room
      debug: import.meta.env.DEV ? 2 : 0,
      config: { iceServers },
    });

    this.peer.on('open', (myId) => {
      this.onStatus('connected');
      // Always re-fetch peer list — also fires after peer.reconnect(), which
      // re-registers this peer with the signaling server but does NOT
      // re-establish any data channels with existing room members.
      void this.fetchAndConnectToPeers(myId);
    });

    this.peer.on('connection', (conn) => this.setupConnection(conn));

    this.peer.on('error', (err) => {
      // Dead zones and a peer who already left are routine. Toasting each
      // one covers the map for the whole canyon.
      if (TRANSIENT_PEER_ERRORS.has(err.type)) {
        this.armSignalingReconnect();
        return;
      }
      this.onStatus('error', err.message);
    });

    this.peer.on('disconnected', () => {
      this.onStatus('disconnected');
      this.armSignalingReconnect();
    });
  }

  /** Keep trying the signaling socket until it sticks or the room is left. */
  private armSignalingReconnect(): void {
    if (this.destroyed || this.signalingTimer !== null) return;
    this.signalingTimer = window.setTimeout(() => {
      this.signalingTimer = null;
      if (this.destroyed || !this.peer || this.peer.destroyed) return;
      if (!this.peer.disconnected) return;
      try {
        this.peer.reconnect();
      } catch {
        /* still offline */
      }
      if (this.peer.disconnected) this.armSignalingReconnect();
    }, 2_000);
  }

  /** Signal just returned. Rejoin the room and dial everyone we already knew. */
  private recover(): void {
    if (this.destroyed || !this.peer || this.peer.destroyed) return;
    if (this.peer.disconnected) {
      this.armSignalingReconnect();
      return;
    }
    if (this.peer.id) void this.fetchAndConnectToPeers(this.peer.id);
    for (const peerId of this.knownPeers) {
      if (!this.connections.has(peerId)) this.scheduleRedial(peerId);
    }
  }

  private async fetchAndConnectToPeers(myId: string): Promise<void> {
    try {
      const protocol = SIGNAL_SECURE ? 'https' : 'http';
      const signalPath = normalizeSignalPath(SIGNAL_PATH);
      const res = await fetch(
        `${protocol}://${SIGNAL_HOST}:${SIGNAL_PORT}${signalPath}${encodeURIComponent(this.roomCode)}/peers`,
      );
      if (!res.ok) throw new Error(`peers ${res.status}`);
      const peers = (await res.json()) as string[];
      for (const peerId of peers) {
        if (peerId !== myId && !this.connections.has(peerId)) {
          const conn = this.peer!.connect(peerId, { reliable: true });
          this.setupConnection(conn);
        }
      }
    } catch {
      this.schedulePeerListRetry();
    }
  }

  private schedulePeerListRetry(): void {
    if (this.destroyed || this.peerListTimer !== null) return;
    this.peerListTimer = window.setTimeout(() => {
      this.peerListTimer = null;
      if (this.destroyed || !this.peer || this.peer.destroyed || this.peer.disconnected || !this.peer.id) return;
      void this.fetchAndConnectToPeers(this.peer.id);
    }, 5_000);
  }

  private setupConnection(conn: DataConnection): void {
    conn.on('open', () => {
      this.connections.set(conn.peer, conn);
      this.knownPeers.add(conn.peer);
      this.openedAt.set(conn.peer, Date.now());
      // Successful open resets the redial backoff for this peer.
      this.redialAttempts.delete(conn.peer);
      this.onPeerOpen?.(conn.peer);
    });

    conn.on('data', (raw) => {
      const msg = parseMessage(typeof raw === 'string' ? raw : JSON.stringify(raw));
      if (msg) this.onMessage(msg, conn.peer);
    });

    conn.on('close', () => {
      // Mutual redial can briefly create two channels to the same peer; only
      // the one currently in the map is authoritative. A superseded duplicate
      // closing must not evict the live channel or announce peer:left.
      if (this.connections.get(conn.peer) === conn) {
        this.connections.delete(conn.peer);
        document.dispatchEvent(new CustomEvent('peer:left', { detail: conn.peer }));
      }
      // Attempt to re-establish the data channel with backoff — recovers from
      // LTE↔WiFi switches and transient ICE failures without a reload. Also
      // continues the backoff chain when a redial attempt closes unopened.
      if (!this.connections.has(conn.peer)) {
        this.scheduleRedial(conn.peer);
      }
    });

    conn.on('error', () => {
      // 'error' is normally followed by 'close', which handles re-dial.
      // Remove from connections here so we don't try to send on a broken channel.
      if (this.connections.get(conn.peer) === conn) {
        this.connections.delete(conn.peer);
      }
    });
  }

  /**
   * The channel looks open but nothing has arrived. Close it so the close
   * handler dials again. A parked car still sends every few seconds, so a
   * long silence is a dead tunnel, not a stop light.
   */
  reopen(peerId: string): void {
    const opened = this.openedAt.get(peerId) ?? 0;
    // A channel that just came back is still waiting for the next fix.
    if (this.connections.has(peerId) && Date.now() - opened < 20_000) return;
    const conn = this.connections.get(peerId);
    if (!conn) {
      this.scheduleRedial(peerId);
      return;
    }
    try {
      conn.close();
    } catch {
      if (this.connections.get(peerId) === conn) this.connections.delete(peerId);
      this.scheduleRedial(peerId);
    }
  }

  private scheduleRedial(peerId: string): void {
    if (this.destroyed || !this.peer || this.peer.destroyed) return;
    // Don't queue a second timer if one is already pending for this peer.
    if (this.redialTimers.has(peerId)) return;
    const attempt = (this.redialAttempts.get(peerId) ?? 0) + 1;
    const delay = redialDelay(attempt);
    this.redialAttempts.set(peerId, attempt);
    const timer = window.setTimeout(() => {
      this.redialTimers.delete(peerId);
      // Skip if already reconnected (peer may have re-dialled us first).
      if (this.destroyed || this.connections.has(peerId) || !this.peer || this.peer.destroyed) return;
      if (this.peer.disconnected) {
        // Hold the attempt count. The canyon is not a failed dial.
        this.redialAttempts.set(peerId, Math.max(0, attempt - 1));
        this.armSignalingReconnect();
        this.scheduleRedial(peerId);
        return;
      }
      try {
        const conn = this.peer.connect(peerId, { reliable: true });
        this.setupConnection(conn);
      } catch {
        this.scheduleRedial(peerId);
      }
    }, delay);
    this.redialTimers.set(peerId, timer);
  }

  /**
   * `attempted` is open data channels. `sent` is how many accepted the bytes.
   * That is queueing, not proof the other app showed the message.
   */
  broadcast(msg: P2PMessage): { attempted: number; sent: number } {
    const data = serializeMessage(msg);
    let attempted = 0;
    let sent = 0;
    for (const conn of this.connections.values()) {
      if (!conn.open) continue;
      attempted += 1;
      try {
        conn.send(data);
        sent += 1;
      } catch {
        /* closed between the open check and the send */
      }
    }
    return { attempted, sent };
  }

  sendToPeer(peerId: string, msg: P2PMessage): void {
    const conn = this.connections.get(peerId);
    if (!conn?.open) return;
    try {
      conn.send(serializeMessage(msg));
    } catch {
      /* ignore closed */
    }
  }

  getMyId(): string | null {
    return this.peer?.id ?? null;
  }

  getPeerCount(): number {
    return this.connections.size;
  }

  getPeerIds(): string[] {
    return [...this.connections.keys()];
  }

  disconnect(): void {
    this.destroyed = true;
    window.removeEventListener('online', this.onOnline);
    if (this.signalingTimer !== null) window.clearTimeout(this.signalingTimer);
    this.signalingTimer = null;
    if (this.peerListTimer !== null) window.clearTimeout(this.peerListTimer);
    this.peerListTimer = null;
    for (const timer of this.redialTimers.values()) {
      window.clearTimeout(timer);
    }
    this.redialTimers.clear();
    this.redialAttempts.clear();
    this.knownPeers.clear();
    this.openedAt.clear();
    for (const conn of this.connections.values()) {
      try {
        conn.close();
      } catch {
        /* ignore */
      }
    }
    this.connections.clear();
    this.peer?.destroy();
    this.peer = null;
    this.onStatus('disconnected');
  }

  /** Create a throttled broadcast function for location messages */
  locationBroadcaster(): (msg: LocationMessage) => void {
    return throttleLocation((msg) => this.broadcast(msg), LOCATION_THROTTLE_MS);
  }
}
